import { UserPermissions } from './permissions';
import { ROLE_LABELS, type Role } from '../../../lib/rbacRoles';

export type { Role };
export { ROLE_LABELS };

/** Role groups for Users section tabs: Providers (with supervisors), Clients, Staff (everyone else). */
export type UserRoleGroup = 'providers' | 'clients' | 'staff';

export const PROVIDER_ROLES: Role[] = ['provider', 'supervisor'];
export const CLIENT_ROLES: Role[] = ['client'];
export const STAFF_ROLES: Role[] = [
  'unify-admin',
  'practice-owner',
  'practice-admin',
];

export function getUserRoleGroup(role: Role): UserRoleGroup {
  if (PROVIDER_ROLES.includes(role)) return 'providers';
  if (CLIENT_ROLES.includes(role)) return 'clients';
  return 'staff';
}

/** Functional roles a directory user holds (falls back to primary `role`). */
export function heldRolesForUser(user: Pick<User, 'role' | 'heldRoles'>): Role[] {
  if (user.heldRoles !== undefined) return [...user.heldRoles];
  return [user.role];
}

/**
 * True when a settings update only syncs role / heldRoles (persisted by set-role
 * / set-primary-role — profile save must not rewrite the Practitioner).
 */
export function isRoleMembershipOnlyUpdate(updates: Partial<User>): boolean {
  const keys = Object.keys(updates);
  return keys.length > 0 && keys.every((key) => key === 'role' || key === 'heldRoles' || key === 'status');
}

/** True when any held role belongs to the Providers / Clients / Staff tab. */
export function userMatchesRoleGroup(
  user: Pick<User, 'role' | 'heldRoles'>,
  group: UserRoleGroup,
): boolean {
  return heldRolesForUser(user).some((role) => getUserRoleGroup(role) === group);
}

/** Directory row is a Practitioner (not a Patient/client). */
export function userIsPractitionerEntry(user: Pick<User, 'role'>): boolean {
  return user.role !== 'client';
}

export interface InsuranceCredential {
  id: string;
  insuranceCompany: string;
  providerId: string;
  status: 'active' | 'pending' | 'inactive';
  effectiveDate: string;
}

export interface PracticeLocation {
  id: string;
  name: string;
  /** Street address line 1 (set by normalization when legacy address is present). */
  addressLine1?: string;
  /** Street address line 2 (e.g. suite, unit). */
  addressLine2?: string;
  /** Legacy single-line address; normalized to addressLine1/addressLine2 on load. */
  address?: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  /** Latitude for map pin (optional; can be set via geocoding). */
  latitude?: number;
  /** Longitude for map pin (optional; can be set via geocoding). */
  longitude?: number;
  status?: 'active' | 'inactive';
  hours?: import('@unify/fhir-model/resources/location').LocationWeeklyHours;
}

export interface LegalDocument {
  id: string;
  name: string;
  type: 'w9' | 'ein' | 'liability-insurance' | 'business-license' | 'articles-of-incorporation' | 'w9-form' | 'unify-contract' | 'general-liability-umbrella-policy' | 'epli' | 'other';
  uploadedDate: string;
  expirationDate?: string;
  fileUrl?: string;
}

export interface PortalTile {
  id: string;
  title: string;
  imageUrl?: string;
  link: string;
  order: number;
}

/** Profile tab — persisted inside `clinical.demographics` JSON on Practitioner (admin-actions bot). */
export interface ProfileDemographics {
  genderIdentity?: string;
  lgbtqiaIdentities?: string[];
  religion?: string[];
  raceEthnicity?: string[];
  preferredAgeGroups?: string[];
  languagesSpoken?: string[];
  clinicalSpecialties?: string[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: Role;
  /**
   * All functional role slugs from profile `role` extensions.
   * Primary is `role` / `heldRoles[0]`. Users with multiple held roles show all in the Role column.
   */
  heldRoles?: Role[];
  practiceId: string;
  practiceName: string;
  status: 'Active' | 'Inactive' | 'Pending';
  createdAt: string;
  lastActive: string;
  permissions?: Partial<UserPermissions>; // Individual permission overrides as boolean object
  bio?: string; // Provider biography
  /** Other names or aliases (e.g. maiden name, other names the provider goes by). */
  aliases?: string[];
  /** Preferred first/last for Practitioner.name nickname; not stored on clinical JSON. */
  preferredNames?: { first?: string; last?: string };
  /** Distinct legal name when `name` is a preferred label (directory / UserTable). */
  legalName?: string;
  credentialing?: InsuranceCredential[]; // Insurance credentialing
  billsThrough?: string; // User ID this person bills through
  billsThroughName?: string; // Name of user this person bills through
  reportsTo?: string; // User ID this person reports to
  reportsToName?: string; // Name of user this person reports to
  locationIds?: string[]; // Practice location IDs this user works at
  /** Provider clinical/credentialing data (Verifiable, Salesforce, CAQH). */
  clinical?: {
    /** National Provider Identifier (10 digits). Required for HIPAA transactions. */
    npi?: string;
    /** DEA number (prescribers of controlled substances). */
    deaNumber?: string;
    /** CAQH ProView ID; used by Verifiable for intake. */
    caqhId?: string;
    /** Clinical specialty / taxonomy (e.g. Clinical Psychologist, LCSW). */
    specialty?: string;
    /** Primary license state (e.g. CA, NY). */
    licenseState?: string;
    /** Primary professional license number. */
    licenseNumber?: string;
    /** Primary license expiration date (YYYY-MM-DD). */
    licenseExpiration?: string;
    /** CAQH ProView username. */
    caqhUsername?: string;
    /** CAQH ProView password (store encrypted in production). */
    caqhPassword?: string;
    /** Last CAQH attestation date. */
    lastCaqhAttestation?: string;
    /** Next CAQH attestation due date (read-only / calculated). */
    nextCaqhAttestationDueDate?: string;
    /** Profile — date of birth (YYYY-MM-DD). */
    dateOfBirth?: string;
    /** Profile — postal zip / code. */
    zipCode?: string;
    /** Profile — preferred first name (display). */
    preferredFirstName?: string;
    /** Profile — preferred last name (display). */
    preferredLastName?: string;
    /** Profile — gender, checkboxes, etc. */
    demographics?: ProfileDemographics;
  };
}

/** Practice profile design variant for UI */
export type PracticeProfileDesignVariant = 'clean' | 'modern';

/** Audit metadata for practice profile updates */
export interface PracticeProfileAudit {
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

export interface Practice {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  userCount: number;
  createdAt: string;
  status: 'Active' | 'Inactive';
  bio?: string;
  /** Legal entity name (billing, documents). */
  legalName?: string;
  /** Primary contact email. */
  email?: string;
  /** Primary contact phone. */
  phone?: string;
  /** Public website URL (Organization.telecom[system=url]). */
  websiteUrl?: string;
  /** Tax ID (EIN). Required. */
  taxId?: string;
  /** NPI if required at practice level. Optional. */
  npi?: string;
  /** Legal entity address (billing, documents). Editable on profile; independent of Locations. */
  legalEntityAddress?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  /** @deprecated Prefer legalEntityAddress. Location ID used for Legal Entity Address if legalEntityAddress not set. */
  primaryLocationId?: string;
  locations?: PracticeLocation[];
  legalDocuments?: LegalDocument[];
  portalTiles?: PortalTile[];
  /** Who/when last updated profile (audit). */
  profileAudit?: PracticeProfileAudit;
}

// Generic RBAC permission metadata types (UI-focused; currently not wired to API types).
export type PermissionObject = string;
export type PermissionAction = string;
export type ResourceScope = string;

export interface RolePermissions {
  role: Role;
  permissions: {
    object: PermissionObject;
    actions: PermissionAction[];
    scope: ResourceScope;
  }[];
}